import React, { useState, useCallback, useEffect } from 'react';
import { faArrowUp, faArrowDown, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Pagination } from 'components/reports/controls/Pagination';
import Skeleton from '@material-ui/lab/Skeleton';
import { useUserDashboardUsers } from '../useUserDashboardUsers';
import {
  DepartmentSummary,
  SortField,
  UserRoleDashboard,
  UserStatus,
} from '../types';

interface Props {
  departments: DepartmentSummary[];
  initialDepartmentId?: number;
  onDepartmentFilterChange?: (departmentId: number | undefined) => void;
}

const ROLE_LABELS: Record<UserRoleDashboard, string> = {
  admin: 'Admin',
  course_manager: 'Course Manager',
  content_manager: 'Content Manager',
  course_viewer: 'Course Viewer',
  editor: 'Editor',
  learner: 'Learner',
};

const STATUS_LABELS: Record<UserStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  never_logged_in: 'Never Logged In',
};

const STATUS_STYLES: Record<UserStatus, { bg: string; text: string; dot: string }> = {
  active: { bg: '#f0fdf4', text: '#15803d', dot: '#22c55e' },
  inactive: { bg: '#f9fafb', text: '#6b7280', dot: '#d1d5db' },
  never_logged_in: { bg: '#fffbeb', text: '#b45309', dot: '#f59e0b' },
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRelativeDate = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

export const UserDashboardTable: React.FC<Props> = ({
  departments,
  initialDepartmentId,
  onDepartmentFilterChange,
}) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('last_logged_in_at');
  const [sortDesc, setSortDesc] = useState(true);
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [roleFilter, setRoleFilter] = useState<UserRoleDashboard | ''>('');
  const [departmentFilter, setDepartmentFilter] = useState<number | ''>(initialDepartmentId ?? '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    setDepartmentFilter(initialDepartmentId ?? '');
    setPage(1);
  }, [initialDepartmentId]);

  const sortString = `${sortDesc ? '-' : ''}${sortField}`;

  const { users, meta, loading, error } = useUserDashboardUsers({
    page,
    per_page: perPage,
    sort: sortString,
    filter: {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(roleFilter ? { role: roleFilter } : {}),
      ...(departmentFilter ? { department_id: departmentFilter as number } : {}),
      ...(searchQuery ? { search: searchQuery } : {}),
    },
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortField(field);
      setSortDesc(true);
    }
    setPage(1);
  };

  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput);
    setPage(1);
  }, [searchInput]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  const updateDepartmentFilter = (value: number | '') => {
    setDepartmentFilter(value);
    onDepartmentFilterChange?.(value === '' ? undefined : value);
    setPage(1);
  };

  const clearAllFilters = () => {
    setStatusFilter('');
    setRoleFilter('');
    updateDepartmentFilter('');
    setSearchInput('');
    setSearchQuery('');
  };

  const hasActiveFilters = statusFilter || roleFilter || departmentFilter || searchQuery;

  const gridColumns = 'minmax(160px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(110px, 1fr) minmax(100px, 1fr)';

  const selectStyle: React.CSSProperties = {
    fontSize: 13,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '6px 10px',
    backgroundColor: '#fff',
    color: '#374151',
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', backgroundColor: '#fff' }}>
      {/* Header + Filters */}
      <div style={{ padding: '20px 24px 16px', backgroundColor: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>Users</span>
          {meta && (
            <span style={{ fontSize: 12, color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{meta.total_count} total</span>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 180px', maxWidth: 260 }}>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: 10,
              display: 'flex', alignItems: 'center', pointerEvents: 'none',
            }}>
              <FontAwesomeIcon icon={faSearch} style={{ fontSize: 10, color: '#d1d5db' }} />
            </div>
            <input
              type="text"
              placeholder="Search name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onBlur={handleSearch}
              style={{
                width: '100%',
                paddingLeft: 28, paddingRight: 28, paddingTop: 6, paddingBottom: 6,
                fontSize: 13,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                color: '#374151',
                boxSizing: 'border-box',
              }}
            />
            {searchInput && (
              <button
                onClick={clearSearch}
                style={{
                  position: 'absolute', top: 0, bottom: 0, right: 8,
                  display: 'flex', alignItems: 'center',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  color: '#d1d5db',
                }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 9 }} />
              </button>
            )}
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as UserStatus | ''); setPage(1); }}
            style={selectStyle}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="never_logged_in">Never Logged In</option>
          </select>

          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value as UserRoleDashboard | ''); setPage(1); }}
            style={selectStyle}
          >
            <option value="">All Roles</option>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {departments.length > 0 && (
            <select
              value={departmentFilter}
              onChange={(e) => {
                updateDepartmentFilter(e.target.value ? Number(e.target.value) : '');
              }}
              style={selectStyle}
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          )}

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              style={{
                fontSize: 12, color: '#6B7280', background: 'none', border: 'none',
                cursor: 'pointer', padding: '6px 4px',
              }}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflow: 'hidden', backgroundColor: '#fff' }}>
        {error && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#ef4444' }}>{error}</p>
          </div>
        )}

        {loading && (
          <div style={{ padding: '16px 24px' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={52} style={{ borderRadius: 4, marginBottom: 4 }} />
            ))}
          </div>
        )}

        {!loading && !error && (
          <div style={{ backgroundColor: '#fff' }}>
            {/* Header row */}
            <div style={{
              display: 'grid', gridTemplateColumns: gridColumns,
              padding: '0 24px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff',
            }}>
              <div
                style={{ padding: '8px 0', fontSize: 12, fontWeight: 500, color: '#6b7280', cursor: 'pointer' }}
                onClick={() => handleSort('name')}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Name
                  {sortField === 'name' && (
                    <FontAwesomeIcon icon={sortDesc ? faArrowDown : faArrowUp} style={{ fontSize: 8, color: '#6B7280' }} />
                  )}
                </span>
              </div>
              <div style={{ padding: '8px 0', fontSize: 12, fontWeight: 500, color: '#6b7280' }}>Role</div>
              <div style={{ padding: '8px 0', fontSize: 12, fontWeight: 500, color: '#6b7280' }}>Department</div>
              <div style={{ padding: '8px 0', fontSize: 12, fontWeight: 500, color: '#6b7280' }}>Status</div>
              <div
                style={{ padding: '8px 0', fontSize: 12, fontWeight: 500, color: '#6b7280', cursor: 'pointer' }}
                onClick={() => handleSort('last_logged_in_at')}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Last Login
                  {sortField === 'last_logged_in_at' && (
                    <FontAwesomeIcon icon={sortDesc ? faArrowDown : faArrowUp} style={{ fontSize: 8, color: '#6B7280' }} />
                  )}
                </span>
              </div>
              <div
                style={{ padding: '8px 0', fontSize: 12, fontWeight: 500, color: '#6b7280', cursor: 'pointer' }}
                onClick={() => handleSort('created_at')}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Created
                  {sortField === 'created_at' && (
                    <FontAwesomeIcon icon={sortDesc ? faArrowDown : faArrowUp} style={{ fontSize: 8, color: '#6B7280' }} />
                  )}
                </span>
              </div>
            </div>

            {/* Data rows */}
            {users.length === 0 ? (
              <div style={{ padding: '64px 24px', textAlign: 'center', fontSize: 13, color: '#6B7280' }}>
                No users found matching your filters.
              </div>
            ) : (
              users.map((user) => {
                const statusStyle = STATUS_STYLES[user.status];
                return (
                  <div
                    key={user.id}
                    style={{
                      display: 'grid', gridTemplateColumns: gridColumns, alignItems: 'center',
                      padding: '0 24px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#fff',
                    }}
                  >
                    <div style={{ padding: '12px 0' }}>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#111827', margin: 0, lineHeight: 1.4 }}>{user.name || '—'}</p>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0', lineHeight: 1.4 }}>{user.email || '—'}</p>
                    </div>
                    <div style={{ padding: '12px 0', fontSize: 13, color: '#374151' }}>
                      {ROLE_LABELS[user.role] || user.role}
                    </div>
                    <div style={{ padding: '12px 0', fontSize: 13, color: '#6b7280' }}>
                      {user.departments.length > 0
                        ? user.departments.map((d, i) => (
                          <span key={d.id}>
                            {i > 0 && ', '}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateDepartmentFilter(d.id);
                              }}
                              style={{
                                background: 'none', border: 'none', padding: 0,
                                cursor: 'pointer', fontSize: 13, color: '#3b82f6',
                                textDecoration: departmentFilter === d.id ? 'underline' : 'none',
                              }}
                              title={`Filter by ${d.name}`}
                            >
                              {d.name}
                            </button>
                          </span>
                        ))
                        : '—'}
                    </div>
                    <div style={{ padding: '12px 0' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '2px 10px', borderRadius: 99,
                        fontSize: 12, fontWeight: 500,
                        backgroundColor: statusStyle.bg, color: statusStyle.text,
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          backgroundColor: statusStyle.dot, flexShrink: 0,
                        }} />
                        {STATUS_LABELS[user.status]}
                      </span>
                    </div>
                    <div style={{ padding: '12px 0' }}>
                      <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.4 }}>{formatRelativeDate(user.last_logged_in_at)}</p>
                      {user.last_logged_in_at && (
                        <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0', lineHeight: 1.4 }}>{formatDate(user.last_logged_in_at)}</p>
                      )}
                    </div>
                    <div style={{ padding: '12px 0', fontSize: 13, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                      {formatDate(user.created_at)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {meta && meta.total_count > 0 && (
        <div style={{ padding: '0 24px 16px', borderTop: '1px solid #f3f4f6', backgroundColor: '#fff' }}>
          <Pagination
            totalCount={meta.total_count}
            page={meta.current_page}
            pageSize={perPage}
            goToPageOne={() => setPage(1)}
            goToPreviousPage={() => setPage(Math.max(1, page - 1))}
            goToNextPage={() => setPage(Math.min(meta.total_pages, page + 1))}
            goToLastPage={() => setPage(meta.total_pages)}
            onPageSizeChange={(newSize) => {
              setPerPage(newSize);
              setPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
};
